import React from "react";
import PropTypes from "prop-types";
import { Redirect, useHistory } from "react-router-dom";

//Material imports
import Grid from "@mui/material/Grid";
import { ThemeProvider } from "@mui/material/styles";

//Limitless imports
import { ExpertLifecycleProvider } from "../../../expert";
import { useLoginWrapperContext } from "@limitless/expert";
import { useExpertProfileContext } from "../../../expert/hooks/expert-profile/ExpertProfileContext";
import { useAuthenticationContext } from "../../../expert/hooks/authentication/AuthenticationContext";
import GraphQLProvider from "../../clients/GraphQLProvider";
import Header from "../header/Header";
import LeftNavigation from "../navigation/left-navigation/LeftNavigation";
import { checkRouteValidity, policyCheck } from "../../utils/ScreenHelper";
import ActivityMonitor from "../hocs/ActivityMonitor";
import { NavigationContextProvider } from "../../hooks/navigation/NavigationContext";
import { POLICY_MFA_REQUIRED } from "../../utils/CommonPolicyConstants";
import { createTheme } from "../../theme/Theme";
import SecurityProfileChangeDialog from "../dialogs/security-profile-change-dialog/SecurityProfileChangeDialog";

//Modern Imports
import Box from "@limitless/modern/layout/Box";
import TopNav from "../../components-modern/top-nav/TopNav";
import LeftNav from "../../components-modern/navigation/left-nav/LeftNav";
import { modernTheme } from "@limitless/themeing/modern-theme/ModernTheme";
const Screen = ({
  navItems,
  renderHeaderOptions,
  renderSettingsDrawer,
  children,
  mode,
  helpDrawerAvailable = false
}) => {
  const {
    auth: {
      authenticated
    },
    signOut,
    updateAuthError,
    updateGraphReady,
    appAccessPolicy
  } = useAuthenticationContext();
  const {
    canUserAccess,
    profile,
    isDarkMode
  } = useExpertProfileContext();
  const history = useHistory();
  const [allowAppAccess] = React.useState(canUserAccess(appAccessPolicy));
  React.useEffect(() => {
    if ((!allowAppAccess || !policyCheck(navItems, history.location.pathname)) && authenticated) {
      signOut("You are not authorised to view this portal");
    }
  }, [allowAppAccess, history.location.pathname]);
  const handleUnauthorisedGraphQLAccess = () => {
    history.push("/");
    updateAuthError("Your session has expired");
  };
  let allowRouteAccess = false;
  let hideNavigation = false;
  if (allowAppAccess) {
    const routeDetails = checkRouteValidity(navItems, history.location.pathname);
    allowRouteAccess = routeDetails.isRouteValid;
    if (allowRouteAccess) {
      hideNavigation = routeDetails.location.hideNavigation ? routeDetails.location.hideNavigation : false;
      allowRouteAccess = allowAppAccess && routeDetails.location.policy ? canUserAccess(routeDetails.location.policy) : true;
    }
  }
  //remember the children so they don't re-render every time screen changes
  const screen = React.useMemo(() => {
    return children;
  }, []);
  const theme = React.useMemo(() => modernTheme(isDarkMode ? "dark" : "light"), [isDarkMode]);
  const originalTheme = createTheme();
  const isPortalSelection = history.location.pathname === "/portal-selection";
  return /*#__PURE__*/React.createElement(GraphQLProvider, {
    onUnauthorised: handleUnauthorisedGraphQLAccess,
    updateGraphReady: updateGraphReady
  }, allowRouteAccess && authenticated ? /*#__PURE__*/React.createElement(NavigationContextProvider, null, /*#__PURE__*/React.createElement(ExpertLifecycleProvider, null, mode === "expert" ? /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      gridTemplateColumns: isPortalSelection ? "none" : "80px 1fr",
      display: "grid",
      height: "100%",
      backgroundColor: theme => theme.palette.background.navigation
    }
  }, !isPortalSelection && /*#__PURE__*/React.createElement(Grid, {
    sx: {
      gridColumns: 1,
      boxShadow: theme => `0px 4px 15px -2px ${theme.palette.background.shadow}`,
      zIndex: 1
    }
  }, /*#__PURE__*/React.createElement(LeftNav, {
    navItems: navItems
  })), /*#__PURE__*/React.createElement(Grid, {
    sx: {
      gridColumns: 2,
      backgroundColor: theme => theme.palette.background.default
    }
  }, /*#__PURE__*/React.createElement(TopNav, {
    helpDrawerAvailable: helpDrawerAvailable,
    renderHeaderOptions: renderHeaderOptions,
    navItems: navItems
  }), /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: originalTheme
  }, screen)))) : /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Header, {
    renderHeaderOptions: renderHeaderOptions,
    renderSettingsDrawer: renderSettingsDrawer,
    mode: hideNavigation ? "portal" : mode,
    helpDrawerAvailable: helpDrawerAvailable
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      width: "100%"
    }
  }, !hideNavigation && /*#__PURE__*/React.createElement(LeftNavigation, {
    navItems: navItems
  }), screen)))), /*#__PURE__*/React.createElement(SecurityProfileChangeDialog, null))) : /*#__PURE__*/React.createElement(Redirect, {
    from: "*",
    to: {
      pathname: "/"
    }
  }), /*#__PURE__*/React.createElement(ActivityMonitor, null));
};
export default Screen;
Screen.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
  navItems: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  renderSettingsDrawer: PropTypes.func,
  renderHeaderOptions: PropTypes.func,
  mode: PropTypes.string.isRequired,
  appAccessPolicy: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  helpDrawerAvailable: PropTypes.bool
};